import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import SuperAdminService from "../../services/SuperAdminService";
import type {
  CreateRolePayload,
  SuperAdminRole,
  UpdateRolePayload,
} from "../../services/SuperAdminService";
import {
  RBAC_ACTIONS,
  RBAC_MODULES,
} from "../../config/permissions";
import type {
  PermissionAction,
  PermissionMatrixEntry,
  PermissionModule,
} from "../../config/permissions";
import LoadingSpinner from "../../components/LoadingSpinner";
import {
  useSuperAdminOutletContext,
} from "./SuperAdminLayout";

interface RoleFormState {
  id?: string;
  name: string;
  description?: string;
  permissions: PermissionMatrixEntry[];
}

const createEmptyPermissions = (): PermissionMatrixEntry[] =>
  RBAC_MODULES.map((module) => ({
    module: module.id,
    actions: {
      read: false,
      create: false,
      update: false,
      delete: false,
    },
  }));

const createManagerPermissions = (): PermissionMatrixEntry[] =>
  RBAC_MODULES.map((module) => {
    // Manager has access to operational modules but not system admin modules
    const isOperational = [
      "dashboard",
      "drivers",
      "vehicles",
      "assignments",
      "inspections",
      "damages",
      "fines",
      "reports",
      "fuelCards",
    ].includes(module.id);

    if (isOperational) {
      return {
        module: module.id,
        actions: {
          read: true,
          create: true,
          update: true,
          delete: true,
        },
      };
    }

    return {
      module: module.id,
      actions: {
        read: false,
        create: false,
        update: false,
        delete: false,
      },
    };
  });

const createViewerPermissions = (): PermissionMatrixEntry[] =>
  RBAC_MODULES.map((module) => {
    // Viewer can read everything except system admin settings
    const isSystem = ["users", "roles", "organizations", "settings"].includes(module.id);
    
    if (!isSystem) {
      return {
        module: module.id,
        actions: {
          read: true,
          create: false,
          update: false,
          delete: false,
        },
      };
    }

    return {
      module: module.id,
      actions: {
        read: false,
        create: false,
        update: false,
        delete: false,
      },
    };
  });

const permissionsToMap = (permissions: PermissionMatrixEntry[]) => {
  const map = new Map<PermissionModule, PermissionMatrixEntry>();
  permissions.forEach((permission) => {
    map.set(permission.module, permission);
  });
  return map;
};

const normalizePermissions = (
  permissions: PermissionMatrixEntry[]
): PermissionMatrixEntry[] => {
  const map = permissionsToMap(permissions);
  return RBAC_MODULES.map((module) => {
    if (map.has(module.id)) {
      const existing = map.get(module.id)!;
      return {
        module: module.id,
        actions: {
          read: Boolean(existing.actions.read),
          create: Boolean(existing.actions.create),
          update: Boolean(existing.actions.update),
          delete: Boolean(existing.actions.delete),
        },
      };
    }
    return {
      module: module.id,
      actions: {
        read: false,
        create: false,
        update: false,
        delete: false,
      },
    };
  });
};

type AccessLevel = "none" | "read" | "manage" | "custom";

const getAccessLevel = (actions: { read: boolean; create: boolean; update: boolean; delete: boolean }): AccessLevel => {
  if (actions.read && actions.create && actions.update && actions.delete) return "manage";
  if (actions.read && !actions.create && !actions.update && !actions.delete) return "read";
  if (!actions.read && !actions.create && !actions.update && !actions.delete) return "none";
  return "custom";
};

const RolesPage = () => {
  const {
    selectedOrganizationId,
    selectedOrganizationLabel,
    organizations,
  } = useSuperAdminOutletContext();
  const queryClient = useQueryClient();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formState, setFormState] = useState<RoleFormState>({
    name: "",
    description: "",
    permissions: createEmptyPermissions(),
  });
  const [isEditing, setIsEditing] = useState(false);

  const {
    data: roles = [],
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["super-admin", "roles", selectedOrganizationId],
    queryFn: () =>
      SuperAdminService.fetchRoles(selectedOrganizationId as string),
    enabled: Boolean(selectedOrganizationId),
  });

  const createRoleMutation = useMutation({
    mutationFn: ({ organizationId, payload }: { organizationId: string; payload: CreateRolePayload }) =>
      SuperAdminService.createRole(organizationId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries([
        "super-admin",
        "roles",
        selectedOrganizationId,
      ]);
      setIsDialogOpen(false);
      setFormState({
        name: "",
        description: "",
        permissions: createEmptyPermissions(),
      });
      setIsEditing(false);
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ roleId, payload }: { roleId: string; payload: UpdateRolePayload }) =>
      SuperAdminService.updateRole(roleId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries([
        "super-admin",
        "roles",
        selectedOrganizationId,
      ]);
      setIsDialogOpen(false);
      setIsEditing(false);
    },
  });

  const deleteRoleMutation = useMutation({
    mutationFn: (roleId: string) => SuperAdminService.deleteRole(roleId),
    onSuccess: () => {
      queryClient.invalidateQueries([
        "super-admin",
        "roles",
        selectedOrganizationId,
      ]);
    },
  });

  const handleAccessLevelChange = (
    module: PermissionModule,
    level: AccessLevel
  ) => {
    setFormState((prev) => ({
      ...prev,
      permissions: prev.permissions.map((permission) => {
        if (permission.module !== module) return permission;

        let newActions = { ...permission.actions };
        if (level === "none") {
          newActions = { read: false, create: false, update: false, delete: false };
        } else if (level === "read") {
          newActions = { read: true, create: false, update: false, delete: false };
        } else if (level === "manage") {
          newActions = { read: true, create: true, update: true, delete: true };
        }
        
        return { ...permission, actions: newActions };
      }),
    }));
  };

  const applyTemplate = (template: "empty" | "manager" | "viewer") => {
    let newPermissions: PermissionMatrixEntry[] = [];
    
    if (template === "manager") {
      newPermissions = createManagerPermissions();
    } else if (template === "viewer") {
      newPermissions = createViewerPermissions();
    } else {
      newPermissions = createEmptyPermissions();
    }

    setFormState(prev => ({
      ...prev,
      permissions: newPermissions
    }));
  };

  const openCreateDialog = () => {
    setFormState({
      name: "",
      description: "",
      permissions: createEmptyPermissions(),
    });
    setIsEditing(false);
    setIsDialogOpen(true);
  };

  const openEditDialog = (role: SuperAdminRole) => {
    setFormState({
      id: role.id,
      name: role.name,
      description: role.description,
      permissions: normalizePermissions(role.permissions),
    });
    setIsEditing(true);
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setIsEditing(false);
  };

  const handleFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    if (!selectedOrganizationId) return;

    const payload: CreateRolePayload = {
      name: formState.name,
      description: formState.description,
      permissions: formState.permissions,
    };

    if (isEditing && formState.id) {
      updateRoleMutation.mutate({
        roleId: formState.id,
        payload,
      });
    } else {
      createRoleMutation.mutate({
        organizationId: selectedOrganizationId,
        payload,
      });
    }
  };

  const handleDeleteRole = (role: SuperAdminRole) => {
    deleteRoleMutation.mutate(role.id);
  };

  const organizationName = useMemo(() => {
    if (!selectedOrganizationId) return "";
    return (
      organizations.find((org) => org.id === selectedOrganizationId)?.name ||
      selectedOrganizationLabel ||
      ""
    );
  }, [organizations, selectedOrganizationId, selectedOrganizationLabel]);

  if (!selectedOrganizationId) {
    return <LoadingSpinner message="Chargement de l'organisation par défaut..." />;
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" fontWeight={700}>
          Gestion des Rôles
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
        >
          Nouveau rôle
        </Button>
      </Stack>

      <Paper elevation={0} sx={{ mt: 3 }}>
        {isLoading || isFetching ? (
          <LoadingSpinner message="Chargement des rôles..." />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nom</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.map((role) => (
                <TableRow key={role.id} hover>
                  <TableCell>{role.name}</TableCell>
                  <TableCell>{role.description || "—"}</TableCell>
                  <TableCell>{role.isDefault ? "Défaut" : "Personnalisé"}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Modifier">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(role)}
                          disabled={role.isDefault}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Supprimer">
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteRole(role)}
                          disabled={role.isDefault}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog open={isDialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>{isEditing ? "Modifier le rôle" : "Nouveau rôle"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary">Modèles rapides :</Typography>
                <Chip 
                  label="Manager (Opérations)" 
                  onClick={() => applyTemplate("manager")} 
                  color="primary" 
                  variant="outlined"
                  size="small"
                  clickable
                />
                <Chip 
                  label="Observateur (Lecture seule)" 
                  onClick={() => applyTemplate("viewer")} 
                  color="info" 
                  variant="outlined"
                  size="small"
                  clickable
                />
                <Chip 
                  label="Vider" 
                  onClick={() => applyTemplate("empty")} 
                  variant="outlined"
                  size="small"
                  clickable
                />
            </Stack>

            <TextField
              label="Nom du rôle"
              name="name"
              value={formState.name}
              onChange={handleFieldChange}
              fullWidth
              required
            />
            <TextField
              label="Description"
              name="description"
              value={formState.description}
              onChange={handleFieldChange}
              fullWidth
              multiline
              rows={2}
            />
            <Box sx={{ overflowX: "auto", maxHeight: "400px" }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", width: "60%" }}>
                      Module
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", width: "40%" }}>
                      Niveau d'accès
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formState.permissions.map((permission) => {
                    const moduleMeta = RBAC_MODULES.find(
                      (module) => module.id === permission.module
                    );
                    const accessLevel = getAccessLevel(permission.actions);
                    
                    return (
                      <TableRow key={permission.module} hover>
                        <TableCell component="th" scope="row">
                          {moduleMeta?.label || permission.module}
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={accessLevel === "custom" ? "read" : accessLevel}
                              onChange={(e) => handleAccessLevelChange(permission.module, e.target.value as AccessLevel)}
                              displayEmpty
                            >
                              <MenuItem value="none">Aucun accès</MenuItem>
                              <MenuItem value="read">Lecture seule</MenuItem>
                              <MenuItem value="manage">Contrôle total</MenuItem>
                              {accessLevel === "custom" && (
                                <MenuItem value="custom" disabled>Personnalisé</MenuItem>
                              )}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Annuler</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              createRoleMutation.isLoading || updateRoleMutation.isLoading
            }
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RolesPage;
